import { zipSync, strToU8 } from 'fflate';
import { composeInvitationMarkdown } from './invitationEmail';

/**
 * A job as the recruiting export had it: seven files under one base name.
 *
 * The files are named so the ZIP import groups them back into one job. What the
 * dashboard can edit (mail, Indeed, AfA) comes as it stands now; what it cannot
 * (both HTML files, the JSON, the storyboard) comes as uploaded.
 */

/** In export order, which is also the order the files are listed in. */
const KINDS = [
  { kind: 'campaign', label: 'Kampagnenseite', suffix: '.html', type: 'text/html' },
  { kind: 'json', label: 'Google-Jobs-JSON', suffix: '_GoogleJobs.json', type: 'application/json' },
  { kind: 'assessment', label: 'Fragebogen 2', suffix: '_Fragebogen2.html', type: 'text/html' },
  { kind: 'invitation', label: 'Einladungs-Mail', suffix: '_Email_Einladung_Fragebogen2.md', type: 'text/markdown' },
  { kind: 'indeed', label: 'Indeed-Text', suffix: '_Indeed.txt', type: 'text/plain' },
  { kind: 'afa', label: 'AfA-Text', suffix: '_AfA.txt', type: 'text/plain' },
  { kind: 'storyboard', label: 'Storyboard', suffix: '_Storyboard.md', type: 'text/markdown' },
];

/**
 * The job's base name as the export had it.
 *
 * The import stores it. Jobs imported before that fall back to the campaign
 * page's file name, which the CMS renames on collisions, then to the slug.
 */
export function packageBaseName({ job, documents }) {
  return (
    documents?.baseName ||
    job?.campaignFilename?.replace(/\.html?$/i, '') ||
    job?.slug ||
    `stelle-${job?.id}`
  );
}

/** The export name of one kind of file, e.g. jobFilename('indeed', { job, documents }). */
export function jobFilename(kind, sources) {
  return `${packageBaseName(sources)}${KINDS.find((k) => k.kind === kind).suffix}`;
}

/**
 * A text file ends with a line break, as every file in the export does. The
 * stored ad texts do not (the import trims them, the text field may too), so
 * it is put back here.
 */
export function asTextFile(text) {
  return text.endsWith('\n') ? text : `${text}\n`;
}

/** Text content per kind, or undefined when there is nothing to hand out. */
function textFor(kind, { job, documents, assessment }) {
  switch (kind) {
    case 'json':
      return documents?.googleJobsJson || undefined;
    case 'storyboard':
      return documents?.storyboardMd || undefined;
    case 'indeed':
      return documents?.indeedText ? asTextFile(documents.indeedText) : undefined;
    case 'afa':
      return documents?.afaText ? asTextFile(documents.afaText) : undefined;
    case 'invitation':
      if (!assessment?.emailSubject && !assessment?.emailBody) return undefined;
      return composeInvitationMarkdown({
        original: documents?.emailMarkdown,
        subject: assessment.emailSubject,
        body: assessment.emailBody,
        title: job?.role || job?.title,
      });
    default:
      return undefined;
  }
}

/**
 * Every file of one job, available or not.
 *
 * Text files carry their content. The two HTML files live on other servers and
 * carry `remote` instead, so nothing is fetched until someone asks for it.
 */
export function describeJobFiles({ job, documents, assessment }) {
  const baseName = packageBaseName({ job, documents });
  return KINDS.map(({ kind, label, suffix, type }) => {
    const file = { kind, label, type, filename: `${baseName}${suffix}` };
    if (kind === 'campaign') return { ...file, remote: 'campaign', available: Boolean(job?.campaignUrl) };
    if (kind === 'assessment') return { ...file, remote: 'assessment', available: Boolean(assessment?.filename) };
    const text = textFor(kind, { job, documents, assessment });
    return { ...file, text, available: text !== undefined };
  });
}

/** The same files under another base name. */
function withBaseName(files, baseName, next) {
  return files.map((file) => ({ ...file, filename: next + file.filename.slice(baseName.length) }));
}

/**
 * One ZIP from any number of jobs, flat like the export folder.
 *
 * `packages` is [{ baseName, files: [{ filename, data }] }], data as text or
 * bytes. Two jobs with the same base name would overwrite each other, so the
 * later one is renamed rather than lost.
 */
export function zipPackages(packages) {
  const entries = {};
  for (const { baseName, files } of packages) {
    let named = files;
    for (let n = 2; named.some((file) => entries[file.filename]); n += 1) {
      named = withBaseName(files, baseName, `${baseName}-${n}`);
    }
    for (const file of named) {
      entries[file.filename] = typeof file.data === 'string' ? strToU8(file.data) : file.data;
    }
  }
  return zipSync(entries);
}

/** Hand bytes or text to the browser as a file download. */
export function downloadFile(data, filename, type) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
